import React from 'react';
import {
  Lesson,
  DailyRosterInstructor
} from '@/constants/Interfaces/CommonInterfaces';
import { useLessonDetails } from '@/hooks/useLessonDetails';

type LessonDetailsCardProps = {
  lesson: Lesson;
  instructor: DailyRosterInstructor;
};

const LessonDetailsCard: React.FC<LessonDetailsCardProps> = ({
  lesson,
  instructor
}) => {
  const lessonDetails = useLessonDetails(lesson, instructor);
  return (
    <div className='bg-white rounded-lg shadow-xl border border-gray-200 w-[800px] opacity-100'>
      <div className='flex items-center justify-between p-2 border-b border-gray-200 bg-blue-600 rounded-t-lg'>
        <h3 className='text-lg font-semibold text-white'>Lesson Details</h3>
      </div>

      <div className='p-2 space-y-2'>
        <div className='space-y-2'>
          <h4 className='font-medium text-gray-900 text-sm border-b border-gray-200 pb-1'>
            Lesson Information
          </h4>

          <div className='grid grid-cols-11 gap-0 text-sm border border-gray-200 rounded overflow-hidden'>
            <div className='bg-blue-600 text-white p-1 text-center font-medium text-xs'>
              Instructor
            </div>
            <div className='bg-blue-600 text-white p-1 text-center font-medium text-xs'>
              String
            </div>
            <div className='bg-blue-600 text-white p-1 text-center font-medium text-xs'>
              Client
            </div>
            <div className='bg-blue-600 text-white p-1 text-center font-medium text-xs'>
              REQ
            </div>
            <div className='bg-blue-600 text-white p-1 text-center font-medium text-xs'>
              Lesson
            </div>
            <div className='bg-blue-600 text-white p-1 text-center font-medium text-xs'>
              AM
            </div>
            <div className='bg-blue-600 text-white p-1 text-center font-medium text-xs'>
              PM
            </div>
            <div className='bg-blue-600 text-white p-1 text-center font-medium text-xs'>
              FD
            </div>
            <div className='bg-blue-600 text-white p-1 text-center font-medium text-xs'>
              Level
            </div>
            <div className='bg-blue-600 text-white p-1 text-center font-medium text-xs'>
              Start
            </div>
            <div className='bg-blue-600 text-white p-1 text-center font-medium text-xs'>
              End
            </div>

            <div className='p-1 text-center border-r border-gray-200 text-xs break-words whitespace-normal'>
              {lessonDetails.instructor}
            </div>
            <div className='p-1 text-center border-r border-gray-200 text-xs'>
              {lessonDetails.string}
            </div>
            <div className='p-1 text-center border-r border-gray-200 text-xs break-words whitespace-normal'>
              {Number(lessonDetails.string) > 0
                ? `${lessonDetails.string} ${lessonDetails.client}`
                : lessonDetails.client}
            </div>
            <div className='p-1 text-center border-r border-gray-200 text-xs'>
              {lessonDetails.lesson_req}
            </div>
            <div className='p-1 text-center border-r border-gray-200 text-xs'>
              {lessonDetails.lesson}
            </div>
            <div className='p-1 text-center border-r border-gray-200 text-xs'>
              {lessonDetails.am}
            </div>
            <div className='p-1 text-center border-r border-gray-200 text-xs'>
              {lessonDetails.pm}
            </div>
            <div className='p-1 text-center border-r border-gray-200 text-xs'>
              {lessonDetails.fd}
            </div>
            <div className='p-1 text-center border-r border-gray-200 text-xs'>
              {lessonDetails.level}
            </div>
            <div className='p-1 text-center border-r border-gray-200 text-xs'>
              {lessonDetails.start}
            </div>
            <div className='p-1 text-center border-r border-gray-200 text-xs'>
              {lessonDetails.end}
            </div>
          </div>
        </div>

        <div className='space-y-2'>
          <h4 className='font-medium text-gray-900 text-sm border-b border-gray-200 pb-1'>
            Additional Information
          </h4>

          <div className='grid grid-cols-8 gap-0 text-sm border border-gray-200 rounded overflow-hidden'>
            <div className='bg-blue-600 text-white p-1 text-center font-medium text-xs'>
              Hotel
            </div>
            <div className='bg-blue-600 text-white p-1 text-center font-medium text-xs'>
              Car
            </div>
            <div className='bg-blue-600 text-white p-1 text-center font-medium text-xs'>
              Pick-up numbers
            </div>
            <div className='bg-blue-600 text-white p-1 text-center font-medium text-xs'>
              Pick-up
            </div>
            <div className='bg-blue-600 text-white p-1 text-center font-medium text-xs'>
              Mountain
            </div>
            <div className='bg-blue-600 text-white p-1 text-center font-medium text-xs'>
              Agent
            </div>
            <div className='bg-blue-600 text-white p-1 text-center font-medium text-xs'>
              Ref
            </div>
            <div className='bg-blue-600 text-white p-1 text-center font-medium text-xs'>
              Paid
            </div>

            <div className='p-1 text-center border-r border-gray-200 text-xs text-red-600 break-words whitespace-normal'>
              {lessonDetails.hotel}
            </div>
            <div className='p-1 text-center border-r border-gray-200 text-xs text-red-600 break-words whitespace-normal'>
              {lessonDetails.car}
            </div>
            <div className='p-1 text-center border-r border-gray-200 text-xs text-red-600 break-words whitespace-normal'>
              {lessonDetails?.transport_pax || 'N/A'}
            </div>
            <div className='p-1 text-center border-r border-gray-200 text-xs text-red-600'>
              {lessonDetails.pickUpTime}
            </div>
            <div className='p-1 text-center border-r border-gray-200 text-xs break-words whitespace-normal'>
              {lessonDetails.mountain}
            </div>
            <div className='p-1 text-center border-r border-gray-200 text-xs break-words whitespace-normal'>
              {lessonDetails.agent}
            </div>
            <div className='p-1 text-center border-r border-gray-200 text-xs'>
              {lessonDetails.ref}
            </div>
            <div
              className={`p-1 text-center font-medium text-xs ${lessonDetails.paid === 'YES' ? 'text-green-600' : 'text-red-600'}`}
            >
              {lessonDetails.paid}
            </div>
          </div>
        </div>

        <div className='space-y-2'>
          <h4 className='font-medium text-gray-900 text-sm border-b border-gray-200 pb-1'>
            Comments
          </h4>
          <div className='bg-gray-50 p-2 rounded border border-gray-200'>
            <p className='text-xs text-slate-700 leading-relaxed'>
              {lessonDetails.comments}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonDetailsCard;
